import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import "../css/Login.css";
import "../css/dropdown.css";

// Searchable Dropdown Component (Same as Register)
const SearchableDropdown = ({
  options = [],
  value,
  onChange,
  placeholder,
  name,
  error,
  loading,
  disabled,
  required,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  useEffect(() => {
    const selected = options.find((opt) => opt.value == value);
    setSearchTerm(selected ? selected.label : "");
  }, [value, options]);

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (onChange) {
      onChange({ target: { name, value: "" } });
    }
    setIsOpen(true);
  };

  const handleSelect = (selectedValue) => {
    setSearchTerm("");
    setIsOpen(false);
    if (onChange) {
      onChange({ target: { name, value: selectedValue } });
    }
  };

  const displayValue = options.find((opt) => opt.value == value)?.label || "";

  return (
    <div className="searchable-dropdown" ref={dropdownRef}>
      <div className={`input-group ${error ? "error" : ""}`}>
        <div className="input-icon">
          <i className="fas fa-search"></i>
        </div>
        <input
          type="text"
          className="form-input"
          placeholder={placeholder}
          value={displayValue || searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          disabled={disabled || loading}
          readOnly={!isOpen}
          required={required}
        />
        {isOpen && (
          <ul className="dropdown-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li
                  key={option.value}
                  className="dropdown-item"
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </li>
              ))
            ) : (
              <li className="dropdown-item no-results">No results found</li>
            )}
          </ul>
        )}
      </div>
      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}
      {loading && <div className="loading-message">Loading...</div>}
    </div>
  );
};

const Profile = () => {
  const navigate = useNavigate();
  const grage_id = localStorage.getItem("grage_id");
  const server_url = localStorage.getItem("server_url");
  const company_id = localStorage.getItem("company_id") || "2";

  const [formData, setFormData] = useState({
    grage_id: "",
    grage_name: "",
    grage_contact_person_name: "",
    grage_number: "",
    grage_email: "",
    grage_address: "",
    grage_city_id: "",
    grage_state_id: "",
    grage_gst_no: "",
    grage_upi_id: "", // New UPI ID field
    grage_password: "", // Optional field for password change
  });

  const [errors, setErrors] = useState({});
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [stateLoading, setStateLoading] = useState(true);
  const [cityLoading, setCityLoading] = useState(false);

  // Get first letter of garage name for avatar
  const getGarageInitial = () => {
    if (formData.grage_name && formData.grage_name.trim().length > 0) {
      return formData.grage_name.trim().charAt(0).toUpperCase();
    }
    return "G";
  };

  // Load profile data from localStorage
  useEffect(() => {
    const userData = localStorage.getItem("userData");
    if (userData) {
      try {
        const profile = JSON.parse(userData);

        setFormData({
          grage_id: profile.grage_id || grage_id,
          grage_name: profile.grage_name || "",
          grage_contact_person_name: profile.grage_contact_person_name || "",
          grage_number: profile.grage_number || "",
          grage_email: profile.grage_email || "",
          grage_address: profile.grage_address || "",
          grage_city_id: profile.grage_city_id || "",
          grage_state_id: profile.grage_state_id || "",
          grage_gst_no: profile.grage_gst_no || "",
          grage_upi_id: profile.grage_upi_id || "", // Load UPI ID from localStorage
          grage_password: "", // Always empty for password field
        });
      } catch (error) {
        console.error("Error parsing user data:", error);
        toast.error("Error loading profile data");
      }
    } else {
      toast.error("Please login to access profile");
      navigate("/login");
    }
  }, [navigate]);

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      if (!server_url) {
        toast.error("Server URL not configured.");
        return;
      }
      setStateLoading(true);
      try {
        const formDataToSend = new URLSearchParams();
        formDataToSend.append("company_id", company_id || "2"); // Default to 2 if not set

        const response = await axios.post(
          `${server_url}state.php`,
          formDataToSend,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );

        if (response.data.status == "1") {
          const stateOptions = (response.data.result || []).map((state) => ({
            value: state.state_id,
            label: state.state_name,
          }));
          setStates(stateOptions);
        } else {
          if (response.data.status == "0") {
            console.error("State fetch error:", response.data.error);
          }
        }
      } catch (error) {
        console.error("Fetch states error:", error);
        toast.error("Unable to fetch states");
      } finally {
        setStateLoading(false);
      }
    };

    fetchStates();
  }, [server_url, company_id]);

  // Fetch cities when state changes
  useEffect(() => {
    const fetchCities = async () => {
      if (!formData.grage_state_id) {
        setCities([]);
        return;
      }
      setCityLoading(true);
      try {
        const formDataToSend = new URLSearchParams();
        formDataToSend.append("company_id", company_id);
        formDataToSend.append("state_id", formData.grage_state_id);

        const response = await axios.post(
          `${server_url}city.php`,
          formDataToSend,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );

        if (response.data.status == "1") {
          const cityOptions = (response.data.result || []).map((city) => ({
            value: city.city_id,
            label: city.city_name,
          }));
          setCities(cityOptions);
        } else {
          if (response.data.status == "0") {
            console.error("City fetch error:", response.data.error);
          }
          setCities([]);
        }
      } catch (error) {
        console.error("Fetch cities error:", error);
        toast.error("Unable to fetch cities");
        setCities([]);
      } finally {
        setCityLoading(false);
      }
    };

    fetchCities();
  }, [formData.grage_state_id, server_url, company_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Convert GST number to uppercase
    if (name == "grage_gst_no") {
      processedValue = value.toUpperCase();
    }

    setFormData({
      ...formData,
      [name]: processedValue,
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation - only check if required fields are present
    const newErrors = {};

    if (!formData.grage_name.trim()) {
      newErrors.grage_name = "Garage name is required";
    }

    if (!formData.grage_state_id) {
      newErrors.grage_state_id = "State is required";
    }

    if (!formData.grage_city_id) {
      newErrors.grage_city_id = "City is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      // Prepare payload - only send grage_password if it's not empty
      const payload = { ...formData };
      if (!payload.grage_password) {
        delete payload.grage_password; // Remove empty password field
      }

      const formDataToSend = new URLSearchParams();
      for (const key in payload) {
        if (payload[key] !== undefined && payload[key] !== null) {
          formDataToSend.append(key, payload[key]);
        }
      }

      const response = await axios.post(
        `${server_url}updateprofile.php`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (response.data.status == "1") {
        // Update localStorage with new data
        const updatedProfile = { ...formData };
        if (!updatedProfile.grage_password) {
          delete updatedProfile.grage_password;
        }
        localStorage.setItem("userData", JSON.stringify(updatedProfile));

        toast.success(response.data.error || "Profile updated successfully!");

        // Store individual fields in localStorage
        localStorage.setItem("grage_name", formData.grage_name);
        localStorage.setItem("grage_email", formData.grage_email);
        localStorage.setItem("grage_address", formData.grage_address);
        localStorage.setItem(
          "grage_contact_person_name",
          formData.grage_contact_person_name
        );
        localStorage.setItem("grage_city_id", formData.grage_city_id);
        localStorage.setItem("grage_state_id", formData.grage_state_id);
        localStorage.setItem("grage_gst_no", formData.grage_gst_no);
        localStorage.setItem("grage_upi_id", formData.grage_upi_id); // Store UPI ID in localStorage

        // Clear password field after successful update
        setFormData((prev) => ({ ...prev, grage_password: "" }));
      } else {
        toast.error(response.data.error || "Profile update failed");
      }
    } catch (error) {
      console.error("Update profile error:", error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.response?.status == 404) {
        toast.error("Update endpoint not found. Check server URL.");
      } else {
        toast.error("Unable to connect to server. Please try again.");
      }
    }
  };

  return (
    <div className="login-container">
      {/* New Header Section */}
      <div
        style={{
          background: "white",
          padding: "15px",
          borderRadius: "15px",
          margin: "10px 20px ",
          maxWidth: "1200px",
          boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
          border: "2px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: "10px",
        }}
      >
        {/* Left side - Garage info with avatar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flex: 1,
          }}
        >
          {/* Round Avatar with Garage Initial */}
          <div
            style={{
              width: "70px",
              height: "70px",
              background:
                "linear-gradient(135deg, rgb(3 82 161) 0%, rgb(3 82 161) 100%)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "32px",
              color: "white",
              fontWeight: "bold",
              boxShadow: "0 6px 10px rgba(3, 82, 161, 0.3)",
              border: "4px solid white",
              outline: "2px solid #e2e8f0",
            }}
          >
            {getGarageInitial()}
          </div>

          {/* Garage Name and Number */}
          <div style={{ textAlign: "left" }}>
            <h2
              style={{
                margin: "0 0 8px 0",
                fontSize: "1.2rem",
                fontWeight: "700",
                color: "#2d3748",
              }}
            >
              {formData.grage_name || "Garage Name"}
            </h2>
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <i
                  className="fas fa-phone-alt"
                  style={{ color: "#667eea", fontSize: "1rem" }}
                ></i>
                <span
                  style={{
                    color: "#4a5568",
                    fontSize: "1.1rem",
                    fontWeight: "500",
                  }}
                >
                  {formData.grage_number || "Mobile Number"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Status/Edit indicator */}
      </div>

      {/* Form Container */}
      <div
        className="login-form-container"
        style={{ padding: "0rem 1.5rem 1.5rem 1.5rem" }}
      >
        <div className="login-form-wrapper">
          {/* Form Header */}
          <form className="login-form" onSubmit={handleSubmit}>
            {/* Two Column Layout for better organization */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              }}
            >
              {/* Left Column */}
              <div>
                {/* Garage Number (Read-only) */}
                <div className="form-group" style={{ display: "none" }}>
                  <label
                    style={{
                      textAlign: "left",
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#4a5568",
                      fontSize: "0.95rem",
                    }}
                  >
                    Garage Mobile Number *
                  </label>
                  <div className="input-group">
                    <div className="input-icon">
                      <i className="fas fa-id-card"></i>
                    </div>
                    <input
                      type="text"
                      name="grage_number"
                      className="form-input"
                      placeholder="Garage Number"
                      value={formData.grage_number}
                      onChange={handleChange}
                      disabled
                      style={{
                        background: "#f8fafc",
                        color: "#718096",
                        cursor: "not-allowed",
                      }}
                    />
                  </div>
                  <span style={{ color: "#a0aec0", fontSize: "0.85rem" }}>
                    Note: This number cannot be changed.
                  </span>
                </div>

                <div className="form-group">
                  <label
                    style={{
                      textAlign: "left",
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#4a5568",
                      fontSize: "0.95rem",
                    }}
                  >
                    Garage Name *
                  </label>
                  <div className="input-group">
                    <div className="input-icon">
                      <i className="fas fa-building"></i>
                    </div>
                    <input
                      type="text"
                      name="grage_name"
                      className={`form-input ${
                        errors.grage_name ? "error" : ""
                      }`}
                      placeholder="Garage Name *"
                      value={formData.grage_name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  {errors.grage_name && (
                    <div className="error-message">
                      <i className="fas fa-exclamation-circle"></i>
                      {errors.grage_name}
                    </div>
                  )}
                </div>

                {/* Contact Person Name */}
                <div className="form-group">
                  <label
                    style={{
                      textAlign: "left",
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#4a5568",
                      fontSize: "0.95rem",
                    }}
                  >
                    Contact Person Name
                  </label>
                  <div className="input-group">
                    <div className="input-icon">
                      <i className="fas fa-user"></i>
                    </div>
                    <input
                      type="text"
                      name="grage_contact_person_name"
                      className={`form-input ${
                        errors.grage_contact_person_name ? "error" : ""
                      }`}
                      placeholder="Contact Person Name"
                      value={formData.grage_contact_person_name}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.grage_contact_person_name && (
                    <div className="error-message">
                      <i className="fas fa-exclamation-circle"></i>
                      {errors.grage_contact_person_name}
                    </div>
                  )}
                </div>

                {/* Email */}
                <div className="form-group">
                  <label
                    style={{
                      textAlign: "left",
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#4a5568",
                      fontSize: "0.95rem",
                    }}
                  >
                    Email
                  </label>
                  <div className="input-group">
                    <div className="input-icon">
                      <i className="fas fa-envelope"></i>
                    </div>
                    <input
                      type="email"
                      name="grage_email"
                      className={`form-input ${
                        errors.grage_email ? "error" : ""
                      }`}
                      placeholder="Email"
                      value={formData.grage_email}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.grage_email && (
                    <div className="error-message">
                      <i className="fas fa-exclamation-circle"></i>
                      {errors.grage_email}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column */}
              <div>
                {/* State - Searchable Dropdown */}
                <div className="form-group">
                  <label
                    style={{
                      textAlign: "left",
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#4a5568",
                      fontSize: "0.95rem",
                    }}
                  >
                    Select State *
                  </label>
                  <SearchableDropdown
                    name="grage_state_id"
                    options={states}
                    value={formData.grage_state_id}
                    onChange={handleChange}
                    placeholder="Search and Select State *"
                    error={errors.grage_state_id}
                    loading={stateLoading}
                    required
                  />
                </div>

                {/* City - Searchable Dropdown */}
                <div className="form-group">
                  <label
                    style={{
                      textAlign: "left",
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#4a5568",
                      fontSize: "0.95rem",
                    }}
                  >
                    Select City *
                  </label>
                  <SearchableDropdown
                    name="grage_city_id"
                    options={cities}
                    value={formData.grage_city_id}
                    onChange={handleChange}
                    placeholder="Search and Select City *"
                    error={errors.grage_city_id}
                    loading={cityLoading}
                    disabled={!formData.grage_state_id}
                    required
                  />
                  {!formData.grage_state_id && !cityLoading && (
                    <div className="info-message">
                      Please select a state first
                    </div>
                  )}
                </div>

                {/* Address */}
                <div className="form-group">
                  <label
                    style={{
                      textAlign: "left",
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#4a5568",
                      fontSize: "0.95rem",
                    }}
                  >
                    Address
                  </label>
                  <div className="input-group">
                    <div className="input-icon">
                      <i className="fas fa-map-marker-alt"></i>
                    </div>
                    <input
                      type="text"
                      name="grage_address"
                      className={`form-input ${
                        errors.grage_address ? "error" : ""
                      }`}
                      placeholder="Address"
                      value={formData.grage_address}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.grage_address && (
                    <div className="error-message">
                      <i className="fas fa-exclamation-circle"></i>
                      {errors.grage_address}
                    </div>
                  )}
                </div>

                {/* GST No */}
                <div className="form-group">
                  <label
                    style={{
                      textAlign: "left",
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#4a5568",
                      fontSize: "0.95rem",
                    }}
                  >
                    GST No
                  </label>
                  <div className="input-group">
                    <div className="input-icon">
                      <i className="fas fa-file-invoice"></i>
                    </div>
                    <input
                      type="text"
                      name="grage_gst_no"
                      className={`form-input ${
                        errors.grage_gst_no ? "error" : ""
                      }`}
                      placeholder="GST No"
                      value={formData.grage_gst_no}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.grage_gst_no && (
                    <div className="error-message">
                      <i className="fas fa-exclamation-circle"></i>
                      {errors.grage_gst_no}
                    </div>
                  )}
                </div>

                {/* UPI ID - New Field */}
                <div className="form-group">
                  <label
                    style={{
                      textAlign: "left",
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#4a5568",
                      fontSize: "0.95rem",
                    }}
                  >
                    UPI ID
                  </label>
                  <div className="input-group">
                    <div className="input-icon">
                      <i className="fas fa-qrcode"></i>
                    </div>
                    <input
                      type="text"
                      name="grage_upi_id"
                      className={`form-input ${
                        errors.grage_upi_id ? "error" : ""
                      }`}
                      placeholder="UPI ID (e.g., yourname@upi)"
                      value={formData.grage_upi_id}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.grage_upi_id && (
                    <div className="error-message">
                      <i className="fas fa-exclamation-circle"></i>
                      {errors.grage_upi_id}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div style={{ textAlign: "center", marginTop: "0rem" }}>
              <button
                type="submit"
                className="login-button"
                style={{
                  background:
                    "linear-gradient(135deg, rgb(3 82 161) 0%, rgb(3 82 161) 100%)",
                  padding: "16px 40px",
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  borderRadius: "12px",
                  boxShadow: "0 6px 10px rgba(3, 82, 161, 0.3)",
                  transition: "all 0.3s ease",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "12px",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-3px)";
                  e.target.style.boxShadow =
                    "0 10px 25px rgba(3, 82, 161, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 6px 10px rgba(3, 82, 161, 0.3)";
                }}
              >
                <i className="fas fa-save" style={{ fontSize: "1.2rem" }}></i>
                Update Profile
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
